import { motion } from "framer-motion";
import { ArrowUpRight, Bookmark, BookmarkCheck, Check, Sparkles, AlertOctagon } from "lucide-react";
import ImportanceBadge from "./ImportanceBadge";
import { fmtDate } from "../../lib/intelApi";

function Block({ label, children, accent }) {
  if (!children) return null;
  return (
    <div>
      <div className={`font-mono text-[10px] uppercase tracking-[0.22em] mb-1.5 ${accent ? "text-accent" : "text-muted-foreground"}`}>
        {label}
      </div>
      <p className="text-sm md:text-[15px] leading-relaxed text-foreground/85">{children}</p>
    </div>
  );
}

export default function UpdateCard({ item, index = 0, onToggle, compact = false }) {
  const pending = item.status !== "processed";
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-5%" }}
      transition={{ duration: 0.6, delay: Math.min(index, 6) * 0.05, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`update-card-${item.id}`}
      className={`group relative bg-background p-6 md:p-8 flex flex-col gap-5 transition-colors hover:bg-surface ${
        item.read ? "opacity-70 hover:opacity-100" : ""
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <ImportanceBadge level={item.importance} />
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="update-provider">
          {item.technology} <span className="text-accent/70">·</span> {item.category}
        </span>
        {item.breaking_change && (
          <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-destructive">
            <AlertOctagon size={11} /> Breaking
          </span>
        )}
        <span className="ml-auto font-mono text-[11px] tracking-[0.1em] text-muted-foreground" data-testid="update-date">
          {fmtDate(item.published_at)}
        </span>
      </div>

      <h3 className="font-heading font-black text-xl md:text-2xl tracking-tight leading-[1.1] group-hover:text-accent transition-colors">
        <a href={item.url} target="_blank" rel="noopener noreferrer" data-testid="update-title-link">
          {item.title}
        </a>
      </h3>

      {pending ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono uppercase tracking-[0.15em]">
          <Sparkles size={12} className="text-accent animate-pulse" /> AI analysis in progress
        </div>
      ) : (
        <div className="space-y-4">
          <Block label="What changed">{item.ai_summary}</Block>
          {!compact && <Block label="Why it matters">{item.why_it_matters}</Block>}
          {!compact && item.why_it_matters_to_me && (
            <div className="border-l-2 border-accent pl-4">
              <Block label="Why this matters to me" accent>
                {item.why_it_matters_to_me}
              </Block>
            </div>
          )}
          <Block label={item.action_required ? "Recommended action · required" : "Recommended action"}>
            {item.recommendation}
          </Block>
        </div>
      )}

      {item.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {item.tags.map((t) => (
            <span key={t} className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-border text-foreground/60">
              {t}
            </span>
          ))}
        </div>
      )}

      <div className="mt-auto pt-4 border-t border-border flex flex-wrap items-center gap-2">
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="read-source-button"
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] bg-accent text-accent-foreground px-4 py-2.5 hover:bg-foreground hover:text-background transition-colors"
        >
          Read source <ArrowUpRight size={13} />
        </a>
        <button
          type="button"
          data-testid="save-button"
          onClick={() => onToggle?.(item, "saved")}
          className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] border px-4 py-2.5 transition-colors ${
            item.saved ? "border-accent text-accent" : "border-border hover:border-accent hover:text-accent"
          }`}
        >
          {item.saved ? <BookmarkCheck size={13} /> : <Bookmark size={13} />} {item.saved ? "Saved" : "Save"}
        </button>
        <button
          type="button"
          data-testid="mark-read-button"
          onClick={() => onToggle?.(item, "read")}
          className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] border px-4 py-2.5 transition-colors ${
            item.read ? "border-border text-muted-foreground" : "border-border hover:border-accent hover:text-accent"
          }`}
        >
          <Check size={13} /> {item.read ? "Read" : "Mark read"}
        </button>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hidden sm:inline">
          {item.source}
        </span>
      </div>
    </motion.article>
  );
}
